import * as JsSIP from "jssip";
import type { RTCSession } from "jssip/lib/RTCSession";
import type { RTCSessionEvent, UAConfiguration } from "jssip/lib/UA";
import type { IceServer } from "@/shared/api/types";
import type { PhoneInfo as SoftphoneInfo } from "@/shared/api/softphoneApi";
import { callerLabel, parseCaller, type ParsedCaller } from "./callerInfo";
import { SIP_REGISTER_EXPIRES, SOFTPHONE_WS_SERVER_OVERRIDE } from "./softphoneConfig";
import { useSoftphoneStore, type CallInfo, type CallResult } from "./softphoneStore";
import {
  playCue,
  startRingback,
  startRingtone,
  stopAllTones,
  stopRingback,
  stopRingtone,
} from "./tones";

/**
 * Motor SIP/WebRTC (JsSIP) fora da árvore React: um único UA por aba dona do softphone,
 * uma perna principal e, opcionalmente, uma perna de consulta (transferência assistida).
 * Todo o estado observável vai para `useSoftphoneStore`.
 */

type EngineHooks = {
  /** Disparado quando uma chamada é atendida/estabelecida (busca fila de origem, payload…). */
  onCallEstablished?: (call: CallInfo) => void;
  /** Disparado quando a perna principal termina. */
  onCallEnded?: (call: CallInfo, result: CallResult, cause?: string) => void;
  /** Regra do legado: chamada recebida em pausa é recusada. */
  shouldRejectIncoming?: () => boolean;
  /** Registro recusado de forma definitiva (credencial/ramal). */
  onRegistrationFatal?: (message: string) => void;
};

let ua: JsSIP.UA | null = null;
let primary: RTCSession | null = null;
let consult: RTCSession | null = null;
let stopping = false;
let backoffTimer: number | null = null;
let backoffMs = 2000;
let hooks: EngineHooks = {};
let currentPhone: SoftphoneInfo | null = null;
let pendingConsultTarget: string | null = null;
let pendingOutgoingToken: string | null = null;
let autoAnswerTimer: number | null = null;
let wasRegistered = false;

const remoteAudio: { primary: HTMLAudioElement | null; consult: HTMLAudioElement | null } = {
  primary: null,
  consult: null,
};

const store = () => useSoftphoneStore.getState();

export function setEngineHooks(h: EngineHooks) {
  hooks = h;
}

/* ------------------------------------------------------------- áudio -- */

function audioEl(kind: "primary" | "consult"): HTMLAudioElement {
  let el = remoteAudio[kind];
  if (!el) {
    el = document.createElement("audio");
    el.autoplay = true;
    el.setAttribute("data-softphone", kind);
    el.style.display = "none";
    document.body.appendChild(el);
    remoteAudio[kind] = el;
    void applyOutputDevice(el);
  }
  return el;
}

async function applyOutputDevice(el: HTMLAudioElement) {
  const id = store().audio.outputId;
  const withSink = el as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };
  if (!withSink.setSinkId) return;
  try {
    await withSink.setSinkId(id || "");
  } catch {
    /* dispositivo indisponível: mantém o padrão */
  }
}

export function refreshOutputDevice() {
  for (const el of [remoteAudio.primary, remoteAudio.consult]) if (el) void applyOutputDevice(el);
}

export function supportsOutputSelection(): boolean {
  return typeof HTMLMediaElement !== "undefined" && "setSinkId" in HTMLMediaElement.prototype;
}

function attachRemoteAudio(session: RTCSession, kind: "primary" | "consult") {
  const pc = session.connection as RTCPeerConnection | undefined;
  if (!pc) return;
  const el = audioEl(kind);
  const bind = () => {
    const stream = new MediaStream();
    pc.getReceivers().forEach((r) => {
      if (r.track && r.track.kind === "audio") stream.addTrack(r.track);
    });
    if (stream.getTracks().length) {
      el.srcObject = stream;
      void el.play().catch(() => {});
    }
  };
  pc.addEventListener("track", (ev: RTCTrackEvent) => {
    if (ev.streams?.[0]) {
      el.srcObject = ev.streams[0];
      void el.play().catch(() => {});
    } else {
      bind();
    }
  });
  bind();
}

function mediaConstraints(): MediaStreamConstraints {
  const inputId = store().audio.inputId;
  return {
    audio: inputId
      ? { deviceId: { exact: inputId }, echoCancellation: true, noiseSuppression: true, autoGainControl: true }
      : { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    video: false,
  };
}

function iceConfig(): RTCConfiguration {
  const servers = (currentPhone?.iceServers?.servers ?? []) as IceServer[];
  const iceServers: RTCIceServer[] = servers
    .filter((s) => s?.url)
    .map((s) => ({
      urls: s.url,
      ...(s.username ? { username: s.username } : {}),
      ...(s.credential ? { credential: s.credential } : {}),
    }));
  return { iceServers, rtcpMuxPolicy: "require", bundlePolicy: "balanced" };
}

/** Não espera indefinidamente pelo fim da coleta ICE (STUN inacessível trava o INVITE). */
function bindIceTimeout(session: RTCSession) {
  let timer: number | null = null;
  session.on("icecandidate", (e) => {
    if (timer != null) return;
    timer = window.setTimeout(() => e.ready(), 2000);
  });
}

/* ------------------------------------------------------- registro SIP -- */

export function wsServerFor(phone: Pick<SoftphoneInfo, "wsServer">): string | null {
  const ws = SOFTPHONE_WS_SERVER_OVERRIDE || phone.wsServer || null;
  return ws ? String(ws).trim() : null;
}

function regMessage(cause: string | undefined): { fatal: boolean; message: string } {
  const c = String(cause ?? "");
  switch (c) {
    case JsSIP.C.causes.AUTHENTICATION_ERROR:
    case JsSIP.C.causes.REJECTED:
      return { fatal: true, message: "Registro recusado pelo servidor de voz (ramal ou senha inválidos)." };
    case JsSIP.C.causes.NOT_FOUND:
    case JsSIP.C.causes.ADDRESS_INCOMPLETE:
      return { fatal: true, message: "Ramal não encontrado no servidor de voz." };
    case JsSIP.C.causes.CONNECTION_ERROR:
      return { fatal: false, message: "Sem conexão com o servidor de voz. Tentando novamente…" };
    case JsSIP.C.causes.REQUEST_TIMEOUT:
      return { fatal: false, message: "O servidor de voz não respondeu ao registro. Tentando novamente…" };
    default:
      return { fatal: false, message: `Falha no registro do ramal (${c || "erro desconhecido"}). Tentando novamente…` };
  }
}

function clearBackoff() {
  if (backoffTimer != null) {
    window.clearTimeout(backoffTimer);
    backoffTimer = null;
  }
}

function scheduleReRegister() {
  clearBackoff();
  const wait = backoffMs;
  backoffMs = Math.min(60_000, Math.round(backoffMs * 1.8));
  backoffTimer = window.setTimeout(() => {
    backoffTimer = null;
    if (!ua || stopping) return;
    store().patch({ reg: "reconnecting", regAttempt: store().regAttempt + 1 });
    if (ua.isConnected()) ua.register();
    // Se o socket estiver fora, o próprio JsSIP reconecta e registra ao voltar.
  }, wait);
}

export type StartResult = { ok: true } | { ok: false; message: string };

export function startUA(phone: SoftphoneInfo, displayName: string | null): StartResult {
  const ws = wsServerFor(phone);
  if (!ws) return { ok: false, message: "O ramal não tem servidor WebSocket (wsServer) configurado." };
  if (!phone.sipServer) return { ok: false, message: "O ramal não tem servidor SIP (sipServer) configurado." };
  if (typeof window !== "undefined" && window.location.protocol === "https:" && /^ws:\/\//i.test(ws)) {
    return {
      ok: false,
      message: `O servidor de voz usa ws:// (${ws}), bloqueado em páginas https. Configure wss:// no ramal.`,
    };
  }
  if (typeof RTCPeerConnection === "undefined") {
    return { ok: false, message: "Este navegador não suporta WebRTC." };
  }

  if (ua) stopUA(false);
  currentPhone = phone;
  stopping = false;
  backoffMs = 2000;
  wasRegistered = false;

  let socket: JsSIP.WebSocketInterface;
  try {
    socket = new JsSIP.WebSocketInterface(ws);
  } catch {
    return { ok: false, message: `Endereço do servidor de voz inválido: ${ws}` };
  }

  const config: UAConfiguration = {
    sockets: [socket],
    uri: `sip:${phone.phoneNumber}@${phone.sipServer}`,
    authorization_user: phone.phoneNumber,
    password: phone.phonePassword ?? undefined,
    display_name: displayName ?? undefined,
    register: true,
    register_expires: SIP_REGISTER_EXPIRES,
    connection_recovery_min_interval: 2,
    connection_recovery_max_interval: 30,
    session_timers: false,
    user_agent: "PhoneManager Web Softphone",
  };

  try {
    ua = new JsSIP.UA(config);
  } catch (e) {
    return { ok: false, message: `Configuração SIP inválida: ${(e as Error)?.message ?? e}` };
  }

  store().patch({ reg: "connecting", regError: null, regAttempt: 0 });

  ua.on("connecting", () => {
    if (stopping) return;
    const s = store();
    if (s.reg !== "registered") s.patch({ reg: wasRegistered ? "reconnecting" : "connecting" });
  });
  ua.on("connected", () => {
    if (stopping) return;
    backoffMs = 2000;
  });
  ua.on("disconnected", (e) => {
    if (stopping) return;
    store().patch({
      reg: "reconnecting",
      regError: e?.error
        ? `Sem conexão com o servidor de voz (${ws}). Tentando novamente…`
        : "Conexão com o servidor de voz encerrada. Reconectando…",
    });
  });
  ua.on("registered", () => {
    if (stopping) return;
    clearBackoff();
    backoffMs = 2000;
    const first = !wasRegistered;
    wasRegistered = true;
    store().patch({ reg: "registered", regError: null, regAttempt: 0 });
    if (first) playCue("login");
  });
  ua.on("unregistered", () => {
    if (stopping) return;
    store().patch({ reg: "reconnecting" });
  });
  ua.on("registrationFailed", (e) => {
    if (stopping) return;
    const { fatal, message } = regMessage(e?.cause);
    if (fatal) {
      store().patch({ reg: "failed", regError: message });
      playCue("fail");
      stopUA(false);
      hooks.onRegistrationFatal?.(message);
      return;
    }
    store().patch({ reg: "reconnecting", regError: message });
    scheduleReRegister();
  });
  ua.on("newRTCSession", onNewSession);

  ua.start();
  return { ok: true };
}

export function stopUA(unregister = true) {
  clearBackoff();
  stopping = true;
  if (autoAnswerTimer != null) {
    window.clearTimeout(autoAnswerTimer);
    autoAnswerTimer = null;
  }
  const u = ua;
  ua = null;
  for (const s of [consult, primary]) {
    try {
      if (s && !s.isEnded()) s.terminate();
    } catch {
      /* ignore */
    }
  }
  consult = null;
  primary = null;
  stopAllTones();
  if (u) {
    try {
      if (unregister && u.isRegistered()) u.unregister({ all: false });
    } catch {
      /* ignore */
    }
    try {
      u.stop();
    } catch {
      /* ignore */
    }
  }
  if (wasRegistered) playCue("logout");
  wasRegistered = false;
  const s = store();
  s.patch({ reg: s.reg === "failed" ? "failed" : "idle", call: null });
}

export function isUARunning() {
  return ua != null;
}

/* ------------------------------------------------------------ sessões -- */

function newId() {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function requestCallId(req: unknown): string | null {
  const id = (req as { call_id?: string } | null)?.call_id;
  return id ? String(id) : null;
}

function finishCall(result: CallResult, cause?: string) {
  const s = store();
  const call = s.call;
  stopAllTones();
  if (autoAnswerTimer != null) {
    window.clearTimeout(autoAnswerTimer);
    autoAnswerTimer = null;
  }
  if (consult && !consult.isEnded()) {
    try {
      consult.terminate();
    } catch {
      /* ignore */
    }
  }
  consult = null;
  primary = null;
  if (!call) return;
  s.patch({ call: null });
  s.addHistory({
    id: call.id,
    direction: call.direction,
    remoteLabel: callerLabel(call.remote),
    remoteUser: call.remote.user,
    startedAt: call.createdAt,
    answeredAt: call.answeredAt,
    endedAt: Date.now(),
    result,
    cause,
    queue: call.queueFrom?.name ?? call.queueFrom?.queueNumber ?? null,
    campaign: s.screenPop && s.screenPop.receivedAt >= call.createdAt ? s.screenPop.campaign?.name ?? null : null,
  });
  hooks.onCallEnded?.(call, result, cause);
}

function failureText(cause: string | undefined): string {
  const c = String(cause ?? "");
  const C = JsSIP.C.causes;
  switch (c) {
    case C.BUSY:
      return "Destino ocupado.";
    case C.REJECTED:
      return "Chamada recusada.";
    case C.UNAVAILABLE:
    case C.NOT_FOUND:
    case C.ADDRESS_INCOMPLETE:
      return "Destino indisponível ou inexistente.";
    case C.NO_ANSWER:
      return "Sem resposta.";
    case C.USER_DENIED_MEDIA_ACCESS:
      return "Acesso ao microfone negado. Libere o microfone no navegador para atender/ligar.";
    case C.CONNECTION_ERROR:
    case C.REQUEST_TIMEOUT:
      return "Falha de conexão com o servidor de voz durante a chamada.";
    case C.WEBRTC_ERROR:
    case C.BAD_MEDIA_DESCRIPTION:
    case C.INCOMPATIBLE_SDP:
      return "Falha de mídia (WebRTC/codec) na chamada.";
    case C.CANCELED:
      return "Chamada cancelada.";
    default:
      return c ? `Chamada encerrada: ${c}.` : "Chamada encerrada.";
  }
}

function bindPrimary(session: RTCSession) {
  session.on("peerconnection", () => attachRemoteAudio(session, "primary"));
  bindIceTimeout(session);

  session.on("progress", (e: unknown) => {
    if (session.direction === "outgoing") {
      // 183 com SDP = early media do Asterisk (ringback real) → para o ringback local.
      const resp = (e as { response?: { status_code?: number; body?: string } }).response;
      if (resp?.status_code === 183 || resp?.body) stopRingback();
    }
  });
  session.on("accepted", () => {
    stopAllTones();
  });
  session.on("confirmed", () => {
    stopAllTones();
    const s = store();
    if (!s.call) return;
    const answeredAt = s.call.answeredAt ?? Date.now();
    s.patchCall({ state: "active", answeredAt });
    const call = store().call;
    if (call) hooks.onCallEstablished?.(call);
  });
  session.on("hold", (e) => {
    if (e.originator === "local") store().patchCall({ held: true });
  });
  session.on("unhold", (e) => {
    if (e.originator === "local") store().patchCall({ held: false });
  });
  session.on("muted", () => store().patchCall({ muted: true }));
  session.on("unmuted", () => store().patchCall({ muted: false }));
  session.on("getusermediafailed", () => {
    store().patch({ micPermission: "denied", lastError: failureText(JsSIP.C.causes.USER_DENIED_MEDIA_ACCESS) });
  });
  session.on("ended", (e) => {
    if (primary !== session) return;
    const call = store().call;
    const result: CallResult = call?.transferring ? "transferred" : "answered";
    playCue("ended");
    finishCall(result, e?.cause);
  });
  session.on("failed", (e) => {
    if (primary !== session) return;
    const call = store().call;
    const cause = e?.cause;
    let result: CallResult = "failed";
    if (call?.direction === "incoming") {
      result = e?.originator === "local" ? "rejected" : "missed";
    } else if (cause === JsSIP.C.causes.CANCELED) {
      result = "canceled";
    }
    if (result === "failed" && e?.originator !== "local") {
      store().patch({ lastError: failureText(cause) });
      playCue("fail");
    }
    finishCall(result, cause);
  });
}

function onNewSession(e: RTCSessionEvent) {
  const session = e.session;

  // Perna de consulta (transferência assistida) originada por nós.
  if (e.originator === "local" && pendingConsultTarget != null) {
    const target = pendingConsultTarget;
    pendingConsultTarget = null;
    consult = session;
    bindConsult(session, target);
    return;
  }

  // Já em chamada: recusa a nova (o legado fazia o mesmo).
  if (e.originator === "remote" && (primary || store().call)) {
    try {
      session.terminate({ status_code: 486, reason_phrase: "Busy Here" });
    } catch {
      /* ignore */
    }
    return;
  }

  const incoming = e.originator === "remote";
  const req = e.request as unknown as {
    from?: { display_name?: string; uri?: { scheme?: string; user?: string; host?: string } };
    to?: { display_name?: string; uri?: { scheme?: string; user?: string; host?: string } };
  };
  const remoteHeader = incoming ? req.from : req.to;
  const remote: ParsedCaller = parseCaller(remoteHeader?.display_name, remoteHeader?.uri);

  primary = session;
  const call: CallInfo = {
    id: newId(),
    direction: incoming ? "incoming" : "outgoing",
    state: incoming ? "ringing" : "dialing",
    remote,
    sipCallId: requestCallId(e.request),
    softphoneCallId: incoming ? null : pendingOutgoingToken,
    createdAt: Date.now(),
    answeredAt: null,
    muted: false,
    held: false,
    queueFrom: null,
    payload: [],
    consult: null,
    transferring: false,
  };
  pendingOutgoingToken = null;
  store().patch({ call, lastError: null });
  bindPrimary(session);

  if (incoming) {
    if (hooks.shouldRejectIncoming?.()) {
      try {
        session.terminate();
      } catch {
        /* ignore */
      }
      return;
    }
    startRingtone();
    if (currentPhone?.automaticAnswering) {
      autoAnswerTimer = window.setTimeout(() => {
        autoAnswerTimer = null;
        if (primary === session && !session.isEstablished() && !session.isEnded()) answer();
      }, 1000);
    }
  } else {
    startRingback();
    attachRemoteAudio(session, "primary");
  }
}

/* ------------------------------------------------------------- ações -- */

export function call(target: string): { ok: true } | { ok: false; message: string } {
  if (!ua || !ua.isRegistered()) return { ok: false, message: "Ramal não registrado." };
  if (primary || store().call) return { ok: false, message: "Já existe uma chamada em andamento." };
  // Mesmo formato do legado: From display "<ramal>%<uuid>.0" → backend associa ao dial-out (softphonecallid).
  const token = `${newId()}.0`;
  pendingOutgoingToken = token;
  try {
    ua.call(target, {
      fromDisplayName: `${currentPhone?.phoneNumber ?? ""}%${token}`,
      mediaConstraints: mediaConstraints(),
      rtcOfferConstraints: { offerToReceiveAudio: true, offerToReceiveVideo: false },
      pcConfig: iceConfig(),
    });
  } catch (e) {
    pendingOutgoingToken = null;
    return { ok: false, message: `Não foi possível discar: ${(e as Error)?.message ?? e}` };
  }
  return { ok: true };
}

export function answer() {
  const s = primary;
  if (!s || s.direction !== "incoming" || s.isEstablished() || s.isEnded()) return;
  stopRingtone();
  if (autoAnswerTimer != null) {
    window.clearTimeout(autoAnswerTimer);
    autoAnswerTimer = null;
  }
  try {
    s.answer({ mediaConstraints: mediaConstraints(), pcConfig: iceConfig() });
    // Igual ao legado: o status vai para INUSE no atendimento (o "confirmed" chega logo depois).
    store().patchCall({ state: "active", answeredAt: Date.now() });
  } catch (e) {
    store().patch({ lastError: `Falha ao atender: ${(e as Error)?.message ?? e}` });
  }
}

export function reject() {
  const s = primary;
  if (!s) return;
  stopRingtone();
  try {
    s.terminate({ status_code: 486, reason_phrase: "Busy Here" });
  } catch {
    /* ignore */
  }
}

export function hangup() {
  const s = primary;
  if (!s) {
    // Estado órfão (UA caiu no meio): limpa localmente.
    if (store().call) finishCall("failed", "local");
    return;
  }
  try {
    s.terminate();
  } catch {
    finishCall("failed", "local");
  }
}

export function toggleMute() {
  const s = primary;
  if (!s || !s.isEstablished()) return;
  if (s.isMuted().audio) s.unmute({ audio: true });
  else s.mute({ audio: true });
}

export function toggleHold() {
  const s = primary;
  if (!s || !s.isEstablished()) return;
  if (s.isOnHold().local) s.unhold({ useUpdate: false });
  else s.hold({ useUpdate: false });
}

/** DTMF RFC 4733. Como no legado, um prefixo `*`/`#` vai sozinho e o resto 500 ms depois. */
export function sendDtmf(tones: string, session: RTCSession | null = consult ?? primary) {
  if (!session || !session.isEstablished() || !tones) return;
  const first = tones[0];
  if ((first === "*" || first === "#") && tones.length > 1) {
    session.sendDTMF(first, { duration: 120, interToneGap: 80 });
    window.setTimeout(() => sendDtmf(tones.slice(1), session), 500);
    return;
  }
  session.sendDTMF(tones, { duration: 120, interToneGap: 80 });
}

/** Macro do ramal: DTMF com `%sysid%` substituído pelo sysid codificado da chamada (legado). */
export function runMacro(value: string) {
  const raw = store().call?.remote.sysidRaw ?? "";
  sendDtmf(value.replace(/%sysid%/gi, raw));
}

function referHandlers(onDone: (ok: boolean, reason?: string) => void) {
  let settled = false;
  const done = (ok: boolean, reason?: string) => {
    if (settled) return;
    settled = true;
    onDone(ok, reason);
  };
  return {
    requestSucceeded: () => {},
    requestFailed: (e: { cause?: string }) => done(false, e?.cause),
    trying: () => {},
    progress: () => {},
    accepted: () => done(true),
    failed: (e: { cause?: string }) => done(false, e?.cause),
  };
}

/** Transferência cega (SIP REFER) para ramal/fila/número. */
export function blindTransfer(target: string): Promise<void> {
  const s = primary;
  if (!s || !s.isEstablished()) return Promise.reject(new Error("Não há chamada estabelecida para transferir."));
  store().patchCall({ transferring: true });
  return new Promise((resolve, reject) => {
    try {
      s.refer(target, {
        eventHandlers: referHandlers((ok, reason) => {
          if (ok) {
            resolve();
            // O Asterisk encerra a perna após o NOTIFY 200; garante o BYE caso não encerre.
            window.setTimeout(() => {
              if (primary === s && !s.isEnded()) s.terminate();
            }, 1500);
          } else {
            store().patchCall({ transferring: false });
            reject(new Error(reason ? `Transferência recusada (${reason}).` : "Transferência recusada."));
          }
        }),
      });
    } catch (e) {
      store().patchCall({ transferring: false });
      reject(e instanceof Error ? e : new Error(String(e)));
    }
  });
}

function bindConsult(session: RTCSession, target: string) {
  session.on("peerconnection", () => attachRemoteAudio(session, "consult"));
  bindIceTimeout(session);
  attachRemoteAudio(session, "consult");
  store().patchCall({ consult: { target, state: "dialing" } });
  session.on("confirmed", () => store().patchCall({ consult: { target, state: "active" } }));
  const end = (e: { cause?: string; originator?: string }) => {
    if (consult !== session) return;
    consult = null;
    const s = store();
    const transferring = s.call?.transferring;
    s.patchCall({ consult: null });
    if (!transferring && e?.originator !== "local" && e?.cause && e.cause !== JsSIP.C.causes.BYE) {
      s.patch({ lastError: `Consulta: ${failureText(e.cause)}` });
    }
    // Volta para o cliente se a consulta caiu sem transferir.
    if (!transferring && primary && primary.isOnHold().local) primary.unhold({ useUpdate: false });
  };
  session.on("ended", end);
  session.on("failed", end);
}

/** Transferência assistida, passo 1: põe o cliente em espera e liga para o destino. */
export function startConsult(target: string): { ok: true } | { ok: false; message: string } {
  if (!ua || !primary || !primary.isEstablished()) return { ok: false, message: "Não há chamada estabelecida." };
  if (consult) return { ok: false, message: "Já existe uma consulta em andamento." };
  if (!primary.isOnHold().local) primary.hold({ useUpdate: false });
  pendingConsultTarget = target;
  try {
    ua.call(target, {
      fromDisplayName: currentPhone?.phoneNumber ?? undefined,
      mediaConstraints: mediaConstraints(),
      rtcOfferConstraints: { offerToReceiveAudio: true, offerToReceiveVideo: false },
      pcConfig: iceConfig(),
    });
  } catch (e) {
    pendingConsultTarget = null;
    return { ok: false, message: `Não foi possível ligar para ${target}: ${(e as Error)?.message ?? e}` };
  }
  return { ok: true };
}

/** Transferência assistida, passo 2: REFER com Replaces (cliente ↔ destino). */
export function completeAttendedTransfer(): Promise<void> {
  const p = primary;
  const c = consult;
  if (!p || !c || !c.isEstablished()) return Promise.reject(new Error("A consulta ainda não foi atendida."));
  store().patchCall({ transferring: true });
  return new Promise((resolve, reject) => {
    try {
      p.refer(c.remote_identity.uri, {
        replaces: c,
        eventHandlers: referHandlers((ok, reason) => {
          if (ok) {
            resolve();
            window.setTimeout(() => {
              if (consult === c && !c.isEnded()) c.terminate();
              if (primary === p && !p.isEnded()) p.terminate();
            }, 1500);
          } else {
            store().patchCall({ transferring: false });
            reject(new Error(reason ? `Transferência recusada (${reason}).` : "Transferência recusada."));
          }
        }),
      });
    } catch (e) {
      store().patchCall({ transferring: false });
      reject(e instanceof Error ? e : new Error(String(e)));
    }
  });
}

export function cancelConsult() {
  const c = consult;
  if (!c) return;
  try {
    c.terminate();
  } catch {
    /* ignore */
  }
}
